/**
 * RBAC Components
 * Reusable components for permission-based UI rendering and route protection
 */

import { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { usePermissions } from "../contexts/PermissionContext";
import { useAuth } from "../contexts/AuthContext";
import Spinner from "../componentLibrary/Spinner";

// Re-export hooks from usePermission.ts for backward compatibility
export {
  usePermission as useHasPermission,
  useRequireAllPermissions as useHasAllPermissions,
} from "../hooks/usePermission";

interface ProtectedRouteProps {
  children: ReactNode;
  permission?: string | string[]; // Required permission(s) - OR logic if array
  requireAll?: boolean; // If true, require ALL permissions (AND logic)
  fallbackPath?: string; // Where to redirect if permission denied
  showSpinner?: boolean; // Show spinner while loading
  requireAuth?: boolean; // If true, check authentication first (default: true)
}

/**
 * ProtectedRoute Component
 * Wraps routes that require authentication and/or specific permissions
 * Redirects to login if not authenticated, or fallback path if permission denied
 *
 * @example
 * <ProtectedRoute permission="content:create">
 *   <CreateContentPage />
 * </ProtectedRoute>
 *
 * @example Multiple permissions (OR logic)
 * <ProtectedRoute permission={["content:update", "content:delete"]}>
 *   <EditContentPage />
 * </ProtectedRoute>
 *
 * @example Multiple permissions (AND logic)
 * <ProtectedRoute permission={["content:read", "content:update"]} requireAll>
 *   <AdvancedEditPage />
 * </ProtectedRoute>
 *
 * @example Authentication only (no specific permission)
 * <ProtectedRoute>
 *   <ProfilePage />
 * </ProtectedRoute>
 */
export function ProtectedRoute({
  children,
  permission,
  requireAll = false,
  fallbackPath = "/error",
  showSpinner = true,
  requireAuth = true,
}: ProtectedRouteProps) {
  const location = useLocation();
  const { hasPermission, hasAllPermissions, loading } = usePermissions();

  // Check authentication first
  const { isAuthenticated: isAuth, loading: authLoading } = useAuth();
  if (requireAuth) {
    if (authLoading) {
      if (showSpinner) {
        return (
          <div className="flex items-center justify-center min-h-screen">
            <Spinner className="mx-auto" />
          </div>
        );
      }
      return null;
    }

    if (!isAuth) {
      return <Navigate to="/login" replace state={{ from: location }} />;
    }
  }

  if (loading) {
    if (showSpinner) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <Spinner className="mx-auto" />
        </div>
      );
    }
    return null;
  }

  // If no permission specified, just show children (auth check already passed)
  if (!permission) {
    return <>{children}</>;
  }

  // Check permissions
  const permissions = Array.isArray(permission) ? permission : [permission];
  const hasAccess = requireAll
    ? hasAllPermissions(permissions)
    : hasPermission(permissions);

  // Redirect if no access
  if (!hasAccess) {
    return <Navigate to={fallbackPath} replace state={{ from: location }} />;
  }

  return <>{children}</>;
}

interface ConditionalRenderProps {
  children: ReactNode;
  permission?: string | string[]; // Required permission(s) - OR logic if array
  requireAll?: boolean; // If true, require ALL permissions (AND logic)
  fallback?: ReactNode; // What to show if permission denied
  showWhileLoading?: boolean; // Show content while loading
}

/**
 * ConditionalRender Component
 * Conditionally renders children based on permissions
 * Use for hiding/showing UI elements based on permissions
 *
 * @example Show button only if user can create content
 * <ConditionalRender permission="content:create">
 *   <Button>Create Content</Button>
 * </ConditionalRender>
 *
 * @example Show for multiple permissions (OR logic)
 * <ConditionalRender permission={["content:update", "content:delete"]}>
 *   <EditButton />
 * </ConditionalRender>
 *
 * @example Show only if user has ALL permissions (AND logic)
 * <ConditionalRender permission={["content:read", "analytics:read"]} requireAll>
 *   <AdvancedDashboard />
 * </ConditionalRender>
 *
 * @example Show fallback if no permission
 * <ConditionalRender
 *   permission="content:create"
 *   fallback={<div>You don't have permission to create content</div>}
 * >
 *   <CreateButton />
 * </ConditionalRender>
 */
export function ConditionalRender({
  children,
  permission,
  requireAll = false,
  fallback = null,
  showWhileLoading = false,
}: ConditionalRenderProps) {
  const { hasPermission, hasAllPermissions, loading } = usePermissions();

  // While loading, either show nothing or content based on showWhileLoading
  if (loading) {
    return showWhileLoading ? <>{children}</> : null;
  }

  // If no permission specified, always show children
  if (!permission) {
    return <>{children}</>;
  }

  // Check permissions
  const permissions = Array.isArray(permission) ? permission : [permission];
  const hasAccess = requireAll
    ? hasAllPermissions(permissions)
    : hasPermission(permissions);

  // Show children if has access, otherwise fallback
  return hasAccess ? <>{children}</> : <>{fallback}</>;
}
